import { Controller } from "@hotwired/stimulus"
import TomSelect      from "tom-select"
// Nota: o CSS base do Tom Select é importado em `app/tailwind/application.tailwind.css`
// (não aqui), porque o pipeline CSS do projeto é Tailwind CLI separado do bundle JS.

// Select — wrapper sobre Tom Select (https://tom-select.js.org).
//
// Aplicado automaticamente pelo partial `_select.html.erb`. Cobre 4 cenários:
//
//   1. Single estático (default)               → busca client-side + dropdown
//   2. Multi estático (multiple: true)         → tags removíveis + busca
//   3. Single async (url:)                     → fetch JSON ao abrir/digitar
//   4. Multi async (url: + multiple: true)     → tags + busca async
//
// + Modificadores opcionais: create: true (criar entradas), max_items: N,
//   plugins: [...] (drag_drop, restore_on_backspace, etc.).
//
// Por que limpar `class` do <select> antes de `new TomSelect(...)`:
//   Tom Select copia as classes do <select> original para o .ts-wrapper
//   (vide tom-select.ts linha 168). Como o <select> tem classes Tailwind
//   (border, h-10, etc.) para o fallback no-JS, isso causa "card dentro de
//   card" (border dupla). Cache as classes, zere antes do init, e restaure
//   no <select> ao final — o <select> volta a ter o estilo de fallback,
//   mas o .ts-wrapper nasce limpo (controlado só por nossos overrides CSS).
//
// Tokens visuais customizados em `app/tailwind/application.tailwind.css`
// (overrides de .ts-wrapper / .ts-control / .ts-dropdown) — compatíveis com
// light/dark via CSS variables shadcn.
export default class extends Controller {
  static values = {
    url:         { type: String,  default: "" },
    minChars:    { type: Number,  default: 0 },
    create:      { type: Boolean, default: false },
    placeholder: { type: String,  default: "" },
    plugins:     { type: Array,   default: [] },
    maxItems:    { type: Number,  default: 0 },   // 0 = ilimitado
    // Quando true, o `change` NATIVO do <select> não borbulha (stopPropagation) —
    // use com `select:closed` para "salvar no fechar" em vez de "no change". Evita
    // que um form com `change->save` dispare o save a cada troca (o que, junto com
    // re-render, reabria o dropdown). Default false (comportamento padrão).
    saveOnClose: { type: Boolean, default: false },
    // Mensagens i18n (vêm traduzidas do partial _select; default em pt-BR).
    noResultsText:    { type: String, default: "Nenhum resultado" },
    loadingMoreText:  { type: String, default: "Carregando mais…" },
    noMoreResultsText: { type: String, default: "Não há mais resultados" }
  }

  connect() {
    // Evita herança de classes Tailwind do <select> para o .ts-wrapper.
    // Ver comentário acima.
    this._originalClasses = this.element.className
    this.element.className = ""

    // "Salvar no fechar": impede o change nativo do <select> de borbulhar (senão
    // o form com change->save dispararia o save no change imediato). O save vem do
    // evento select:closed (emitido abaixo). Só o change borbulhante é barrado —
    // o valor do <select> é atualizado normalmente para o submit.
    if (this.saveOnCloseValue) {
      this.element.addEventListener("change", (e) => e.stopPropagation())
    }

    const isMultiple = this.element.multiple
    const isAsync    = this.urlValue.length > 0

    const config = {
      placeholder: this.placeholderValue || undefined,
      create:      this.createValue,
      // remove_button: "x" para remover tag em multi. Em single sem create,
      // não faz sentido. Adicionamos clear_button só quando faz sentido (single
      // que não é placeholder).
      plugins: this._defaultPlugins(isMultiple, isAsync),
      // Single: esconde placeholder após selecionar (só o item escolhido fica).
      // Multi: mantém placeholder visível como dica ("Adicione mais tags…").
      hidePlaceholder:  !isMultiple,
      allowEmptyOption: false,           // placeholder option é disabled+hidden no markup
      createOnBlur:     this.createValue,
      maxItems:         isMultiple ? (this.maxItemsValue > 0 ? this.maxItemsValue : null) : 1,
      // openOnFocus + selectOnTab melhoram navegação por teclado
      openOnFocus: true,
      selectOnTab: true,
      // Mensagens i18n (sobrescrevem os textos em inglês do Tom Select e do
      // plugin virtual_scroll).
      render: {
        no_results: (_data, escape) => `<div class="no-results">${escape(this.noResultsTextValue)}</div>`,
        loading_more: () => `<div class="loading-more-results py-2 px-3 text-sm text-muted-foreground">${this.loadingMoreTextValue}</div>`,
        no_more_results: () => `<div class="no-more-results py-2 px-3 text-sm text-muted-foreground">${this.noMoreResultsTextValue}</div>`
      }
    }

    if (isAsync) {
      config.valueField  = "value"
      config.labelField  = "label"
      config.searchField = "label"
      config.preload     = "focus"  // dispara primeira busca ao abrir o dropdown
      // Sem teto de opções: o default do Tom Select é 50, e o virtual_scroll
      // PARA de carregar ao atingir maxOptions (travava ~60). Como a busca é
      // paginada server-side, deixamos o scroll infinito ir até o fim.
      config.maxOptions = null

      // Monta a URL da 1ª página de uma busca. O virtual_scroll usa isto como
      // ponto de partida; páginas seguintes vêm de setNextUrl (abaixo).
      config.firstUrl = (query) => {
        const url = new URL(this.urlValue, window.location.origin)
        if (query) url.searchParams.set("q", query)
        url.searchParams.set("page", "1")
        return url.toString()
      }

      // min_chars capturado em closure — o `load` é function() (this = Tom Select),
      // então não acessa o this do Stimulus.
      const minChars = this.minCharsValue

      // `load` é chamado pela 1ª busca E pelo virtual_scroll (ao rolar até o fim).
      // this.getUrl(query) devolve a próxima URL a buscar (firstUrl na 1ª vez).
      config.load = function (query, callback) {
        if (query.length < minChars) {
          callback()
          return
        }
        const url = this.getUrl(query)
        fetch(url, {
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          credentials: "same-origin"
        })
          .then((r) => (r.ok ? r.json() : Promise.reject(r)))
          .then((body) => {
            const data = Array.isArray(body) ? body : body.data || []
            const next = body && body.meta ? body.meta.next_page : null
            // Registra a próxima página (ou nada = acabou) pro virtual_scroll.
            if (next) {
              const nextUrl = new URL(url, window.location.origin)
              nextUrl.searchParams.set("page", String(next))
              this.setNextUrl(query, nextUrl.toString())
            }
            callback(data)
          })
          .catch(() => callback())
      }
    }

    // Eventos custom no <select> para quem faz auto-save "no fechar" em vez de
    // "no change imediato": `select:closed` dispara ao fechar o dropdown E só se o
    // valor mudou desde que abriu. `select:changed` acompanha toda mudança de valor.
    // Suprime a auto-abertura por foco HERDADO do re-render: quando a linha é
    // re-renderizada (Turbo replace pós auto-save), o navegador mantém o foco na
    // posição e openOnFocus reabriria o dropdown sozinho. Nos primeiros frames após
    // connect ignoramos aberturas (o usuário ainda não interagiu); cliques reais
    // vêm depois e abrem normalmente. Só selects auto-save (saveOnClose) precisam.
    if (this.saveOnCloseValue) {
      this._suppressOpen = true
      // setTimeout (não requestAnimationFrame): rAF é pausado em abas ocultas, o
      // que deixaria _suppressOpen travado em true e o dropdown nunca abriria.
      this._suppressTimer = setTimeout(() => { this._suppressOpen = false }, 120)
    }

    config.onDropdownOpen = () => {
      if (this._suppressOpen) {
        // this.tomSelect pode ainda não estar atribuído se a abertura vier de
        // dentro do construtor; adia o fechamento para o próximo tick.
        queueMicrotask(() => { this.tomSelect?.close(); this.tomSelect?.blur() })
        return
      }
      this._valueOnOpen = this.tomSelect?.getValue()
    }
    config.onDropdownClose = () => {
      const changed = this.tomSelect && this.tomSelect.getValue() !== this._valueOnOpen
      if (changed) this.element.dispatchEvent(new CustomEvent("select:closed", { bubbles: true }))
    }
    config.onChange = (value) => {
      this.element.dispatchEvent(new CustomEvent("select:changed", { bubbles: true, detail: { value } }))
      // No modo saveOnClose, LIMPAR (clear_button → valor vazio) não passa por
      // onDropdownClose, então emitimos select:closed aqui para o save acontecer.
      // Seleções normais salvam no fechar do dropdown (não aqui).
      const isEmpty = value === "" || value == null || (Array.isArray(value) && value.length === 0)
      if (this.saveOnCloseValue && isEmpty) {
        this.element.dispatchEvent(new CustomEvent("select:closed", { bubbles: true }))
      }
    }

    this.tomSelect = new TomSelect(this.element, config)

    // NÃO restauramos `className` aqui. Tom Select adiciona
    // `ts-hidden-accessible` ao className do <select> para escondê-lo;
    // restaurar agora SOBRESCREVERIA essa classe e o <select> nativo voltaria
    // a ser visível ao lado do .ts-wrapper (bug do "duplo select").
    //
    // As classes Tailwind originais ficam guardadas em `_originalClasses` e
    // são restauradas no disconnect, para o caso de Turbo destruir o
    // controller e o <select> ressurgir antes do próximo connect.

    // Turbo cache: o snapshot da página é gravado ANTES do disconnect. Como o
    // Tom Select injeta o `.ts-wrapper` no fluxo do documento, o snapshot já o
    // captura; ao voltar (restore), o Turbo repinta o wrapper cacheado E o
    // connect roda de novo criando OUTRO wrapper — a cada ida-e-volta acumula
    // mais um. A correção é desfazer o Tom Select em `turbo:before-cache`, que
    // dispara antes do snapshot: assim o cache guarda só o <select> original.
    this._beforeCache = () => this._teardown()
    document.addEventListener("turbo:before-cache", this._beforeCache)
  }

  disconnect() {
    clearTimeout(this._suppressTimer)
    document.removeEventListener("turbo:before-cache", this._beforeCache)
    this._teardown()
  }

  // Desfaz o Tom Select e restaura o <select> ao estado pristino. Idempotente:
  // chamado tanto no disconnect (navegação normal) quanto no before-cache
  // (antes do snapshot). Sem isto, navegações Turbo deixam wrappers órfãos.
  _teardown() {
    if (!this.tomSelect) return
    this.tomSelect.destroy()
    this.tomSelect = null
    // Restaura classes Tailwind no <select> para o fallback no-JS funcionar se
    // a página re-renderizar (ou o snapshot for restaurado) sem reaplicar Tom
    // Select. O destroy já removeu `ts-hidden-accessible`, então sobrescrever
    // o className aqui é seguro (não ressuscita o "duplo select").
    if (this._originalClasses != null) {
      this.element.className = this._originalClasses
    }
  }

  // Plugins habilitados por default por cenário.
  _defaultPlugins(isMultiple, isAsync) {
    const userPlugins = this.pluginsValue
    const defaults    = []

    if (isMultiple) {
      defaults.push("remove_button") // "x" em cada tag
    } else {
      // Single: "x" pra limpar a seleção e voltar ao placeholder.
      // Tom Select só renderiza o botão quando há item selecionado.
      defaults.push("clear_button")
    }

    // Async: scroll infinito — carrega a próxima página ao rolar até o fim
    // (pareia com firstUrl/setNextUrl no config.load).
    if (isAsync) defaults.push("virtual_scroll")

    // Dedupe (caso usuário tenha passado o mesmo plugin).
    return [...new Set([...defaults, ...userPlugins])]
  }
}
